import { doc, getDoc, setDoc, updateDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';
import { format } from 'date-fns';

/**
 * Generate a formatted invoice number with prefix and padding
 * @param {number} number - The raw invoice number
 * @returns {string} - Formatted invoice number (e.g., INV-2023-0042)
 */
export const formatInvoiceNumber = (number) => {
  const year = new Date().getFullYear();
  const paddedNumber = String(number).padStart(4, '0');
  return `INV-${year}-${paddedNumber}`;
};

/**
 * Get the next available invoice number from Firestore
 * @param {string} userId - User ID
 * @returns {Promise<string>} - The formatted invoice number
 */
export const getNextInvoiceNumber = async (userId) => {
  try {
    console.log('Getting next invoice number for user:', userId);
    
    // Try to get user's counter document
    const counterRef = doc(db, 'invoiceCounters', userId);
    const counterDoc = await getDoc(counterRef);
    
    let nextNumber;
    
    if (counterDoc.exists()) {
      // Increment the existing counter
      const currentCounter = counterDoc.data();
      nextNumber = (currentCounter.lastNumber || 0) + 1;
      
      // Update the counter in Firestore
      await updateDoc(counterRef, {
        lastNumber: nextNumber,
        lastUpdated: new Date()
      });
    } else {
      // Create new counter document starting at 1
      nextNumber = 1;
      
      // Try to find the highest invoice number in existing invoices
      try {
        const userInvoicesRef = collection(db, 'users', userId, 'invoices');
        const q = query(userInvoicesRef, orderBy('invoiceNumber', 'desc'), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const highestInvoice = querySnapshot.docs[0].data();
          if (highestInvoice.invoiceNumber) {
            // If the highest invoice number is greater than 1, use that + 1
            const numericPart = parseInt(highestInvoice.invoiceNumber.split('-').pop());
            if (!isNaN(numericPart) && numericPart >= 1) {
              nextNumber = numericPart + 1;
            }
          }
        }
      } catch (error) {
        console.error('Error checking existing invoices:', error);
        // Continue with nextNumber = 1 if there's an error
      }
      
      // Create the counter document
      await setDoc(counterRef, {
        lastNumber: nextNumber,
        userId: userId,
        created: new Date(),
        lastUpdated: new Date()
      });
    }
    
    // Format and return the invoice number
    return formatInvoiceNumber(nextNumber);
  } catch (error) {
    console.error('Error getting next invoice number:', error);
    throw error;
  }
};

/**
 * Generate CSS styles for invoice HTML
 * @returns {string} - CSS styles as string
 */
export const getInvoiceStyles = () => {
  return `
    <style>
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 20px;
        color: #333;
        background: white;
      }
      .container {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
      }
      .invoice-header {
        display: flex;
        justify-content: space-between;
        margin-bottom: 40px;
      }
      .invoice-title {
        font-size: 28px;
        color: #4338ca;
        margin: 0 0 5px 0;
      }
      .invoice-subtitle {
        margin-top: 0;
        color: #6b7280;
      }
      .invoice-meta {
        text-align: right;
      }
      .invoice-meta h2 {
        margin: 0 0 10px 0;
      }
      .invoice-meta p {
        margin: 2px 0;
        font-size: 14px;
      }
      .invoice-details {
        margin-bottom: 30px;
      }
      .invoice-details-grid {
        display: flex;
        justify-content: space-between;
      }
      .text-right {
        text-align: right;
      }
      .invoice-table {
        width: 100%;
        border-collapse: collapse;
        margin-bottom: 30px;
      }
      .invoice-table th {
        background-color: #f3f4f6;
        padding: 10px;
        text-align: left;
        border-bottom: 1px solid #e5e7eb;
      }
      .invoice-table td {
        padding: 10px;
        border-bottom: 1px solid #e5e7eb;
      }
      .total-row {
        font-weight: bold;
        background-color: #f9fafb;
      }
      .footer {
        border-top: 1px solid #e5e7eb;
        padding-top: 20px;
        font-size: 14px;
      }
      .payment-details {
        margin-bottom: 15px;
      }
      @media print {
        body {
          padding: 0;
          background: white;
        }
        .container {
          padding: 0;
        }
      }
    </style>
  `;
};

/**
 * Save an invoice to Firestore
 * @param {object} invoiceData - The invoice data to save
 * @param {string} userId - User ID
 * @returns {Promise<string>} - ID of the saved invoice
 */
export const saveInvoice = async (userId, invoiceData) => {
  try {
    if (!userId || !invoiceData) {
      throw new Error('Missing required parameters for saving invoice');
    }
    
    // Ensure we have an invoice number
    if (!invoiceData.invoiceNumber) {
      invoiceData.invoiceNumber = await getNextInvoiceNumber(userId);
    }
    
    // Create a document ID that includes userId for uniqueness
    const invoiceNumPart = invoiceData.invoiceNumber.replace(/[^a-zA-Z0-9]/g, '_');
    const userIdPart = userId.substring(0, 8); // Take first 8 chars of userId
    const docId = `${userIdPart}_${invoiceNumPart}`;
    
    console.log('Saving invoice with document ID:', docId);
    console.log('Full path:', `invoices/${docId}`);
    
    // IMPORTANT: Use root collection with the combined ID
    const invoiceRef = doc(db, 'invoices', docId);
    
    // Save to Firestore - using the data exactly as provided, no additional fields
    try {
      await setDoc(invoiceRef, invoiceData);
      console.log('Invoice saved successfully:', docId);
      return docId;
    } catch (writeError) {
      console.error('Firestore write operation failed:', writeError);
      console.error('Error details:', writeError.code, writeError.message);
      throw new Error(`Firebase write failed: ${writeError.message}`);
    }
  } catch (error) {
    console.error('Error saving invoice:', error);
    throw error;
  }
};

/**
 * Format invoice date entries to HTML table rows
 * @param {Array} formattedEntries - Array of entry data
 * @param {string} rateType - Type of rate (percentage, hourly, fixed)
 * @returns {string} - HTML string of table rows
 */
export const formatInvoiceTableRows = (formattedEntries, rateType) => {
  if (!formattedEntries || !Array.isArray(formattedEntries) || formattedEntries.length === 0) {
    return '';
  }
  
  // Add debugging log for the incoming data
  console.log('🔍 formatInvoiceTableRows received:', {
    entriesCount: formattedEntries.length,
    rateType,
    firstEntry: formattedEntries[0]
  });
  
  // For percentage type, we'll return rows showing collections, then add summary rows
  if (rateType === 'percentage') {
    let totalCollections = 0;
    let percentageRate = 0;
    
    // Generate rows showing patient collections (NO percentage applied)
    const rows = formattedEntries.map(entry => {
      const dateFormatted = entry.date ? format(new Date(entry.date), 'dd/MM/yyyy') : '';
      
      if (entry.patients && Array.isArray(entry.patients)) {
        percentageRate = entry.rateValue || 0; // Capture percentage rate
        
        // Calculate total collections for this day (without percentage)
        const dayCollections = entry.patients.reduce((sum, p) => {
          return sum + ((p.count || 0) * (p.rate || 0));
        }, 0);
        
        totalCollections += dayCollections;
        
        // Format patient details for display
        const patientDetails = entry.patients.map(p => 
          `${p.count || 0} patient${(p.count || 0) !== 1 ? 's' : ''} @ £${(p.rate || 0).toFixed(2)}${p.description ? ` (${p.description})` : ''}`
        ).join(' + ');
        
        return `
          <tr>
            <td>${dateFormatted}</td>
            <td>${patientDetails}</td>
            <td class="text-right">£${dayCollections.toFixed(2)}</td>
          </tr>
        `;
      }
      return '';
    }).join('');
    
    // Calculate the percentage amount
    const percentageAmount = totalCollections * (percentageRate / 100);
    
    // Add summary rows after all entries
    const summaryRows = `
      <tr class="subtotal-row">
        <td colspan="2" class="text-right"><strong>Subtotal (patient fees):</strong></td>
        <td class="text-right"><strong>£${totalCollections.toFixed(2)}</strong></td>
      </tr>
      <tr class="percentage-row">
        <td colspan="2" class="text-right"><strong>Your ${percentageRate}% commission:</strong></td>
        <td class="text-right"><strong>£${percentageAmount.toFixed(2)}</strong></td>
      </tr>
      <tr class="total-row">
        <td colspan="2" class="text-right"><strong>Invoice Total:</strong></td>
        <td class="text-right"><strong>£${percentageAmount.toFixed(2)}</strong></td>
      </tr>
    `;
    
    return rows + summaryRows;
  }
  
  // For all other rate types (per_patient, hourly, fixed), use the original mapping logic
  return formattedEntries.map(entry => {
    const dateFormatted = entry.date ? format(new Date(entry.date), 'dd/MM/yyyy') : '';
    
    if (rateType === 'per_patient') {
      // For per_patient rate, we have multiple patient rows per entry (like percentage but no percentage calculation)
      if (entry.patients && Array.isArray(entry.patients)) {
        return entry.patients.map((patient, idx) => {
          // Calculate subtotal and use it as total (no percentage multiplier)
          if (!patient.subtotal && patient.count && patient.rate) {
            patient.subtotal = patient.count * patient.rate;
          }
          
          // For per_patient, the total is the same as subtotal (no percentage applied)
          if (!patient.total) {
            patient.total = patient.subtotal || 0;
          }
          
          return `
          <tr>
            ${idx === 0 ? `<td rowspan="${entry.patients.length}">${dateFormatted}</td>` : ''}
            <td>${patient.description || ''}</td>
            <td class="text-right">${patient.count || 0}</td>
            <td class="text-right">£${(patient.rate || 0).toFixed(2)}</td>
            <td class="text-right">£${(patient.subtotal || 0).toFixed(2)}</td>
            <td class="text-right">£${(patient.total || 0).toFixed(2)}</td>
          </tr>
        `;
        }).join('');
      }
      return '';
    } else if (rateType === 'hourly') {
      // Add logging for hourly entries
      console.log('⏱️ Hourly entry data:', {
        date: dateFormatted,
        hours: entry.hours,
        rate: entry.rate,
        total: entry.total,
        description: entry.description
      });
      
      return `
        <tr>
          <td>${dateFormatted}</td>
          <td>${entry.description || 'Hygienist services'}</td>
          <td class="text-right">${(entry.hours || 0).toFixed(2)}</td>
          <td class="text-right">£${(entry.rate || 0).toFixed(2)}</td>
          <td class="text-right">£${(entry.total || 0).toFixed(2)}</td>
        </tr>
      `;
    } else { // fixed
      // Add logging for fixed rate entries
      console.log('💰 Fixed rate entry data:', {
        date: dateFormatted,
        rate: entry.rate,
        total: entry.total,
        description: entry.description
      });
      
      return `
        <tr>
          <td>${dateFormatted}</td>
          <td>${entry.description || 'Hygienist services'}</td>
          <td></td>
          <td class="text-right">£${(entry.rate || 0).toFixed(2)}</td>
          <td class="text-right">£${(entry.total || 0).toFixed(2)}</td>
        </tr>
      `;
    }
  }).join('');
};